import type { SyncTombstone } from '../../types/syncRecovery';

const TOMBSTONE_TABLES = new Set([
  'guincho_templates_checklist',
  'guincho_campos_template',
  'guincho_marcacao_imagens',
  'guincho_checklists',
  'guincho_valores_checklist',
]);

const RETIRABLE_TABLES = new Set([
  'guincho_templates_checklist',
  'guincho_campos_template',
  'guincho_checklists',
]);

const TEMPLATES_TABLE = 'guincho_templates_checklist';
const TEMPLATE_FIELDS_TABLE = 'guincho_campos_template';
const CHECKLIST_IMAGES_TABLE = 'guincho_marcacao_imagens';
const CHECKLISTS_TABLE = 'guincho_checklists';
const CHECKLIST_VALUES_TABLE = 'guincho_valores_checklist';

export interface TombstoneBarrierMetadata {
  entityId: string;
  parentId: string | null;
  deletionKind: 'delete' | 'retire';
}

class TombstoneBarrierSet extends Set<string> {
  private readonly metadata: TombstoneBarrierMetadata[] = [];

  addTombstone(row: LocalTombstoneRow): this {
    super.add(row.entity_id);
    this.metadata.push({
      entityId: row.entity_id,
      parentId: row.parent_id,
      deletionKind: row.deletion_kind,
    });
    return this;
  }

  getMetadata(): ReadonlyArray<TombstoneBarrierMetadata> {
    return this.metadata;
  }
}

export interface RemoteTombstoneRow {
  sequence: number;
  event_id: string;
  entity_type: string;
  entity_id: string;
  parent_id: string | null;
  deletion_kind: 'delete' | 'retire';
  metadata: Record<string, unknown> | null;
  deleted_at: string;
}

export interface TombstoneFeedPage {
  cursor: string | null;
  targetHighWatermark: number;
  nextCursor: string | null;
  hasMore: boolean;
  rows: RemoteTombstoneRow[];
}

export interface TombstoneGateway {
  fetch?: (input: {
    afterSequence: number;
    limit: number;
    clientBuild: number;
  }) => Promise<RemoteTombstoneRow[]>;
  fetchPage?: (input: {
    cursor: string | null;
    limit: number;
    clientBuild: number;
  }) => Promise<TombstoneFeedPage>;
}

export interface LocalTombstoneRow {
  id: string;
  empresa_id: string;
  entity_type: string;
  entity_id: string;
  parent_id: string | null;
  sequence: number;
  deletion_kind: 'delete' | 'retire';
  metadata: string | null;
  deleted_at: number;
  applied_at: number;
}

export interface LocalTombstoneCursor {
  id: string;
  empresa_id: string;
  stream: string;
  sequence: number;
  updated_at: number;
  cursor?: string | null;
  target_high_watermark?: number | null;
  active?: boolean;
}

export interface TombstoneLocalRepository {
  getCursor(id: string): Promise<LocalTombstoneCursor | null>;
  saveCursor(cursor: LocalTombstoneCursor): Promise<void>;
  listTombstones(companyId: string): Promise<LocalTombstoneRow[]>;
  getTombstone(id: string): Promise<LocalTombstoneRow | null>;
  saveTombstone(row: LocalTombstoneRow): Promise<void>;
  readTarget(table: string, id: string): Promise<Record<string, unknown> | null>;
  retireTarget(table: string, id: string, deletedAt: number): Promise<boolean>;
  retireTargetIfMatches?(
    table: string,
    id: string,
    deletedAt: number,
    expected: Record<string, unknown>
  ): Promise<boolean>;
  deleteTarget(table: string, id: string): Promise<boolean>;
  deleteTargetIfMatches?(
    table: string,
    id: string,
    expected: Record<string, unknown>
  ): Promise<boolean>;
  applyPageDurably?: (input: {
    rows: LocalTombstoneRow[];
    cursor: LocalTombstoneCursor;
  }) => Promise<{ applied: number; duplicates: number }>;
}

export interface TombstonePullResult {
  cursor: number;
  applied: number;
  duplicates: number;
  barriersByTable: Map<string, Set<string>>;
  nextCursor: string | null;
  targetHighWatermark: number | null;
  hasMore: boolean;
}

const cursorIdentity = (companyId: string, userId: string) => ({
  id: `${companyId}:tombstones:${userId}`,
  stream: `tombstones:${userId}`,
});

const V2_STREAM_PREFIX = 'tombstones:v2:';
const MAX_FEED_PAGE_SIZE = 500;

interface TombstoneV2Checkpoint {
  cursor: string | null;
  targetHighWatermark: number;
  active: boolean;
  replaySequence?: number;
}

export class TombstoneCursorReplayRequiredError extends Error {}

const boundedPageSize = (pageSize: number): number => {
  if (!Number.isSafeInteger(pageSize) || pageSize <= 0) return 200;
  return Math.min(pageSize, MAX_FEED_PAGE_SIZE);
};

const encodeV2Checkpoint = (checkpoint: TombstoneV2Checkpoint): string => (
  `${V2_STREAM_PREFIX}${encodeURIComponent(JSON.stringify(checkpoint))}`
);

const decodeV2Checkpoint = (
  cursor: LocalTombstoneCursor | null
): TombstoneV2Checkpoint | null => {
  if (!cursor) return null;

  const targetHighWatermark = cursor.target_high_watermark;
  let embeddedReplaySequence: number | undefined;
  if (cursor.stream.startsWith(V2_STREAM_PREFIX)) {
    try {
      const embedded = JSON.parse(
        decodeURIComponent(cursor.stream.slice(V2_STREAM_PREFIX.length))
      ) as Partial<TombstoneV2Checkpoint>;
      if (
        embedded.replaySequence !== undefined &&
        Number.isSafeInteger(embedded.replaySequence) &&
        embedded.replaySequence >= 0
      ) {
        embeddedReplaySequence = embedded.replaySequence;
      }
    } catch {
      // A validação completa do stream legado ocorre abaixo quando necessário.
    }
  }

  if (
    cursor.cursor !== undefined &&
    targetHighWatermark !== undefined &&
    targetHighWatermark !== null &&
    (cursor.cursor === null || typeof cursor.cursor === 'string') &&
    cursor.active !== undefined &&
    Number.isSafeInteger(targetHighWatermark) &&
    targetHighWatermark >= 0
  ) {
    return {
      cursor: cursor.cursor ?? null,
      targetHighWatermark,
      active: cursor.active === true,
      replaySequence: embeddedReplaySequence,
    };
  }

  if (!cursor.stream.startsWith(V2_STREAM_PREFIX)) return null;

  try {
    const parsed = JSON.parse(
      decodeURIComponent(cursor.stream.slice(V2_STREAM_PREFIX.length))
    ) as Partial<TombstoneV2Checkpoint>;
    if (
      (parsed.cursor !== null && typeof parsed.cursor !== 'string') ||
      !Number.isSafeInteger(parsed.targetHighWatermark) ||
      (parsed.targetHighWatermark as number) < 0 ||
      typeof parsed.active !== 'boolean'
    ) {
      return null;
    }
    return {
      cursor: parsed.cursor ?? null,
      targetHighWatermark: parsed.targetHighWatermark as number,
      active: parsed.active,
      replaySequence: Number.isSafeInteger(parsed.replaySequence) &&
        (parsed.replaySequence as number) >= 0
        ? parsed.replaySequence as number
        : undefined,
    };
  } catch {
    return null;
  }
};

const sameTombstone = (left: LocalTombstoneRow, right: LocalTombstoneRow): boolean => (
  left.empresa_id === right.empresa_id &&
  left.entity_type === right.entity_type &&
  left.entity_id === right.entity_id &&
  left.parent_id === right.parent_id &&
  left.sequence === right.sequence &&
  left.deletion_kind === right.deletion_kind &&
  left.metadata === right.metadata &&
  left.deleted_at === right.deleted_at
);

const buildBarriers = (rows: ReadonlyArray<LocalTombstoneRow>): Map<string, Set<string>> => {
  const barriers = new Map<string, Set<string>>();
  const latestByEntity = new Map<string, LocalTombstoneRow>();
  rows.forEach(row => {
    const key = `${row.entity_type}\u0000${row.entity_id}`;
    const current = latestByEntity.get(key);
    if (
      !current ||
      row.sequence > current.sequence ||
      (row.sequence === current.sequence && row.id.localeCompare(current.id) > 0)
    ) {
      latestByEntity.set(key, row);
    }
  });

  latestByEntity.forEach(row => {
    const ids = barriers.get(row.entity_type) ?? new TombstoneBarrierSet();
    if (ids instanceof TombstoneBarrierSet) {
      ids.addTombstone(row);
    } else {
      ids.add(row.entity_id);
    }
    barriers.set(row.entity_type, ids);
  });
  return barriers;
};

const hasBlockingBarrier = (
  barriers: ReadonlySet<string> | undefined,
  entityId: string,
  options: { includeRetire?: boolean } = {}
): boolean => {
  if (!barriers || !barriers.has(entityId)) return false;

  const metadata = barriers instanceof TombstoneBarrierSet
    ? barriers.getMetadata().filter(row => row.entityId === entityId)
    : [];

  // Sets recebidos de chamadores antigos só têm o id; eles continuam sendo
  // barreiras válidas. Nos sets produzidos aqui, ambos os tipos de remoção
  // bloqueiam mutações novas; leituras históricas podem ignorar retire.
  if (metadata.length === 0) return true;
  return metadata.some(row => (
    row.deletionKind === 'delete' || (options.includeRetire !== false && row.deletionKind === 'retire')
  ));
};

const hasParentBarrier = (
  barriersByTable: ReadonlyMap<string, ReadonlySet<string>>,
  parentTable: string,
  parentId: unknown,
  options: { includeRetire?: boolean } = {}
): boolean => {
  const normalizedParentId = String(parentId ?? '').trim();
  if (!normalizedParentId) return false;
  return hasBlockingBarrier(barriersByTable.get(parentTable), normalizedParentId, options);
};

const isBlockedByParentBarrier = (
  table: string,
  row: Record<string, unknown>,
  barriersByTable: ReadonlyMap<string, ReadonlySet<string>>,
  options: { includeRetire?: boolean } = {}
): boolean => {
  if (table === TEMPLATE_FIELDS_TABLE || table === CHECKLISTS_TABLE) {
    return hasParentBarrier(barriersByTable, TEMPLATES_TABLE, row.template_id, options);
  }

  if (table === CHECKLIST_IMAGES_TABLE) {
    return (
      hasParentBarrier(barriersByTable, TEMPLATES_TABLE, row.template_id, options) ||
      hasParentBarrier(barriersByTable, TEMPLATE_FIELDS_TABLE, row.campo_id, options) ||
      hasParentBarrier(barriersByTable, CHECKLISTS_TABLE, row.checklist_id, options)
    );
  }

  if (table === CHECKLIST_VALUES_TABLE) {
    return (
      hasParentBarrier(barriersByTable, CHECKLISTS_TABLE, row.checklist_id, options) ||
      hasParentBarrier(barriersByTable, TEMPLATE_FIELDS_TABLE, row.campo_id, options)
    );
  }

  return false;
};

export type TombstoneLocalRelationReader = (
  table: string,
  id: string
) => Promise<Record<string, unknown> | null>;

export const isTombstoneBarrierActive = (
  barriersByTable: ReadonlyMap<string, ReadonlySet<string>>,
  table: string,
  entityId: unknown
): boolean => {
  const normalizedEntityId = String(entityId ?? '').trim();
  if (!normalizedEntityId) return false;
  return hasBlockingBarrier(barriersByTable.get(table), normalizedEntityId);
};

const readLocalRelationSafely = async (
  reader: TombstoneLocalRelationReader,
  table: string,
  id: string
): Promise<Record<string, unknown> | null> => {
  try {
    const row = await reader(table, id);
    return row && typeof row === 'object' && !Array.isArray(row) ? row : null;
  } catch {
    return null;
  }
};

export const filterTombstonedRowsWithLocalRelations = async <T extends { id?: string | null }>(
  table: string,
  rows: ReadonlyArray<T>,
  barriersByTable: ReadonlyMap<string, ReadonlySet<string>>,
  readLocalRelation: TombstoneLocalRelationReader
): Promise<{ accepted: T[]; blocked: T[] }> => {
  const directFilter = filterTombstonedRows(table, rows, barriersByTable);

  // Valores não carregam template_id. Quando há um tombstone de template,
  // checklist e campo locais são a prova mínima para decidir se a row pode
  // ser materializada sem ressuscitar uma relação ancestral.
  if (
    table !== CHECKLIST_VALUES_TABLE ||
    (barriersByTable.get(TEMPLATES_TABLE)?.size ?? 0) === 0 ||
    directFilter.accepted.length === 0
  ) {
    return directFilter;
  }

  const accepted: T[] = [];
  const blocked = [...directFilter.blocked];

  for (const row of directFilter.accepted) {
    const rowRecord = row as Record<string, unknown>;
    const checklistId = String(rowRecord.checklist_id ?? '').trim();
    const campoId = String(rowRecord.campo_id ?? '').trim();
    const localChecklist = checklistId
      ? await readLocalRelationSafely(readLocalRelation, CHECKLISTS_TABLE, checklistId)
      : null;
    const localField = campoId
      ? await readLocalRelationSafely(readLocalRelation, TEMPLATE_FIELDS_TABLE, campoId)
      : null;
    const checklistTemplateId = String(localChecklist?.template_id ?? '').trim();
    const fieldTemplateId = String(localField?.template_id ?? '').trim();

    const checklistTemplateBlocked = isTombstoneBarrierActive(
      barriersByTable,
      TEMPLATES_TABLE,
      checklistTemplateId
    );
    const fieldTemplateBlocked = isTombstoneBarrierActive(
      barriersByTable,
      TEMPLATES_TABLE,
      fieldTemplateId
    );
    const checklistRelationUnknown = !checklistId || !localChecklist || !checklistTemplateId;
    const fieldRelationUnknown = Boolean(campoId && (!localField || !fieldTemplateId));

    if (
      checklistTemplateBlocked ||
      fieldTemplateBlocked ||
      checklistRelationUnknown ||
      fieldRelationUnknown
    ) {
      blocked.push(row);
    } else {
      accepted.push(row);
    }
  }

  return { accepted, blocked };
};

export const filterTombstonedRows = <T extends { id?: string | null }>(
  table: string,
  rows: ReadonlyArray<T>,
  barriersByTable: ReadonlyMap<string, ReadonlySet<string>>,
  options: { historical?: boolean } = {}
): { accepted: T[]; blocked: T[] } => {
  const barriers = barriersByTable.get(table);

  const accepted: T[] = [];
  const blocked: T[] = [];
  rows.forEach(row => {
    const rowRecord = row as Record<string, unknown>;
    if (
      (row.id && hasBlockingBarrier(barriers, row.id, { includeRetire: !options.historical })) ||
      isBlockedByParentBarrier(table, rowRecord, barriersByTable, { includeRetire: !options.historical })
    ) {
      blocked.push(row);
    } else {
      accepted.push(row);
    }
  });
  return { accepted, blocked };
};

export class TombstoneSyncService {
  constructor(
    private readonly gateway: TombstoneGateway,
    private readonly repository: TombstoneLocalRepository,
    private readonly pageSize = 200,
    private readonly maxPages = 50
  ) {}

  async pull(input: {
    companyId: string;
    userId: string;
    clientBuild: number;
  }): Promise<TombstonePullResult> {
    const companyId = input.companyId.trim();
    const userId = input.userId.trim();
    if (
      !companyId ||
      !userId ||
      !Number.isSafeInteger(input.clientBuild) ||
      input.clientBuild <= 0
    ) {
      throw new Error('Escopo invalido para pull de tombstones.');
    }

    const identity = cursorIdentity(companyId, userId);
    const storedCursor = await this.repository.getCursor(identity.id);
    if (storedCursor && (
      storedCursor.id !== identity.id ||
      storedCursor.empresa_id !== companyId ||
      !Number.isSafeInteger(storedCursor.sequence) ||
      storedCursor.sequence < 0
    )) {
      throw new Error('Cursor local de tombstones corrompido ou fora do escopo.');
    }

    if (storedCursor && typeof storedCursor.stream !== 'string') {
      throw new Error('Cursor local de tombstones corrompido ou fora do escopo.');
    }

    if (
      storedCursor &&
      storedCursor.stream !== identity.stream &&
      !storedCursor.stream.startsWith(V2_STREAM_PREFIX)
    ) {
      throw new Error('Cursor local de tombstones corrompido ou fora do escopo.');
    }

    const normalizedInput = { companyId, userId, clientBuild: input.clientBuild };
    if (this.gateway.fetchPage) {
      try {
        return await this.pullV2(normalizedInput, storedCursor, identity, false);
      } catch (error) {
        if (
          !(error instanceof TombstoneCursorReplayRequiredError) ||
          !storedCursor?.stream.startsWith(V2_STREAM_PREFIX)
        ) throw error;
        return this.pullV2(normalizedInput, storedCursor, identity, true);
      }
    }

    return this.pullLegacy(normalizedInput, storedCursor, identity);
  }

  private async pullLegacy(
    input: { companyId: string; userId: string; clientBuild: number },
    storedCursor: LocalTombstoneCursor | null,
    identity: { id: string; stream: string }
  ): Promise<TombstonePullResult> {
    if (!this.gateway.fetch) {
      throw new Error('Gateway de tombstones sem protocolo suportado.');
    }

    let cursor = storedCursor?.sequence ?? 0;
    let applied = 0;
    let duplicates = 0;
    const pageSize = boundedPageSize(this.pageSize);

    for (let page = 0; page < this.maxPages; page += 1) {
      const remoteRows = await this.gateway.fetch({
        afterSequence: cursor,
        limit: pageSize,
        clientBuild: input.clientBuild,
      });
      if (remoteRows.length === 0) break;

      const ordered = [...remoteRows].sort((left, right) => left.sequence - right.sequence);
      for (const remote of ordered) {
        if (
          !Number.isSafeInteger(remote.sequence) ||
          remote.sequence <= cursor ||
          !TOMBSTONE_TABLES.has(remote.entity_type) ||
          !remote.event_id ||
          !remote.entity_id ||
          (remote.deletion_kind !== 'delete' && remote.deletion_kind !== 'retire') ||
          Number.isNaN(Date.parse(remote.deleted_at))
        ) {
          throw new Error('Tombstone remoto invalido ou nao monotono.');
        }

        const deletedAt = Date.parse(remote.deleted_at);
        const localRow: LocalTombstoneRow = {
          id: remote.event_id,
          empresa_id: input.companyId,
          entity_type: remote.entity_type,
          entity_id: remote.entity_id,
          parent_id: remote.parent_id,
          sequence: remote.sequence,
          deletion_kind: remote.deletion_kind,
          metadata: remote.metadata ? JSON.stringify(remote.metadata) : null,
          deleted_at: deletedAt,
          applied_at: Date.now(),
        };
        const existing = await this.repository.getTombstone(localRow.id);
        if (existing && !sameTombstone(existing, localRow)) {
          throw new Error('Tombstone local conflita com o evento remoto.');
        }
        if (!existing) {
          await this.repository.saveTombstone(localRow);
          applied += 1;
        } else {
          duplicates += 1;
        }

        await this.applyLocally(localRow);
        cursor = remote.sequence;
        await this.repository.saveCursor({
          id: identity.id,
          empresa_id: input.companyId,
          stream: identity.stream,
          sequence: cursor,
          updated_at: Date.now(),
        });
      }

      if (remoteRows.length < pageSize) break;
      if (page === this.maxPages - 1) {
        throw new Error('Pull de tombstones excedeu o limite seguro de paginas.');
      }
    }

    const allRows = await this.repository.listTombstones(input.companyId);
    if (allRows.some(row => row.empresa_id !== input.companyId)) {
      throw new Error('Repositorio retornou tombstone de outra empresa.');
    }

    return {
      cursor,
      applied,
      duplicates,
      barriersByTable: buildBarriers(allRows),
      nextCursor: null,
      targetHighWatermark: null,
      hasMore: false,
    };
  }

  private async pullV2(
    input: { companyId: string; userId: string; clientBuild: number },
    storedCursor: LocalTombstoneCursor | null,
    identity: { id: string; stream: string },
    replayFromStart: boolean
  ): Promise<TombstonePullResult> {
    const fetchPage = this.gateway.fetchPage;
    if (!fetchPage) {
      throw new Error('Gateway v2 de tombstones indisponivel.');
    }

    const storedCheckpoint = decodeV2Checkpoint(storedCursor);
    if (storedCursor?.stream.startsWith(V2_STREAM_PREFIX) && !storedCheckpoint) {
      throw new Error('Checkpoint v2 de tombstones corrompido.');
    }

    const replaying = replayFromStart || storedCheckpoint?.replaySequence !== undefined;
    let cursor = replayFromStart ? null : storedCheckpoint?.cursor ?? null;
    let expectedTarget = !replayFromStart && storedCheckpoint?.active
      ? storedCheckpoint.targetHighWatermark
      : null;
    const minimumTarget = storedCheckpoint?.targetHighWatermark ?? 0;
    const sequenceFloor = storedCursor?.sequence ?? 0;
    let lastSequence = replayFromStart
      ? 0
      : storedCheckpoint?.replaySequence ?? sequenceFloor;
    let applied = 0;
    let duplicates = 0;

    for (let pageNumber = 0; pageNumber < this.maxPages; pageNumber += 1) {
      const page = await fetchPage({
        cursor,
        limit: boundedPageSize(this.pageSize),
        clientBuild: input.clientBuild,
      });

      if (!page || page.cursor !== cursor) {
        throw new Error('Resposta de página de tombstones com cursor inconsistente.');
      }

      if (
        typeof page.hasMore !== 'boolean' ||
        (page.nextCursor !== null && typeof page.nextCursor !== 'string') ||
        (typeof page.nextCursor === 'string' && page.nextCursor.trim() === '')
      ) {
        throw new Error('Resposta de tombstones com envelope de paginação invalido.');
      }

      if (
        !Number.isSafeInteger(page.targetHighWatermark) ||
        page.targetHighWatermark < minimumTarget
      ) {
        throw new Error('Resposta de tombstones com target high-watermark regressivo ou invalido.');
      }

      if (expectedTarget !== null && page.targetHighWatermark !== expectedTarget) {
        throw new Error('Target high-watermark de tombstones mudou durante o preflight.');
      }
      expectedTarget = page.targetHighWatermark;

      if (!Array.isArray(page.rows)) {
        throw new Error('Resposta de tombstones sem linhas validas.');
      }

      if (page.hasMore && (!page.nextCursor || page.nextCursor === cursor)) {
        throw new Error('Página de tombstones sem next_cursor progressivo.');
      }

      if (
        !page.hasMore &&
        page.rows.length > 0 &&
        (!page.nextCursor || page.nextCursor === cursor)
      ) {
        throw new Error('Página final de tombstones sem cursor de checkpoint.');
      }

      if (!page.hasMore && page.rows.length === 0 && page.nextCursor !== null) {
        throw new Error('Envelope vazio de tombstones não pode carregar cursor.');
      }

      if (page.hasMore && page.rows.length === 0) {
        throw new Error('Página vazia de tombstones não pode declarar continuidade.');
      }

      const localRows: LocalTombstoneRow[] = [];
      const pageEventIds = new Set<string>();
      let pageLastSequence = lastSequence;
      for (const remote of page.rows) {
        if (
          !Number.isSafeInteger(remote.sequence) ||
          remote.sequence <= 0 ||
          remote.sequence <= pageLastSequence ||
          !TOMBSTONE_TABLES.has(remote.entity_type) ||
          typeof remote.event_id !== 'string' ||
          !remote.event_id.trim() ||
          pageEventIds.has(remote.event_id) ||
          typeof remote.entity_type !== 'string' ||
          typeof remote.entity_id !== 'string' ||
          !remote.entity_id.trim() ||
          (remote.parent_id !== null && typeof remote.parent_id !== 'string') ||
          (remote.deletion_kind !== 'delete' && remote.deletion_kind !== 'retire') ||
          typeof remote.deleted_at !== 'string' ||
          Number.isNaN(Date.parse(remote.deleted_at)) ||
          (remote.metadata !== null &&
            (typeof remote.metadata !== 'object' || Array.isArray(remote.metadata)))
        ) {
          throw new Error('Tombstone remoto invalido no feed v2.');
        }

        pageEventIds.add(remote.event_id);
        pageLastSequence = remote.sequence;
        const deletedAt = Date.parse(remote.deleted_at);
        localRows.push({
          id: remote.event_id,
          empresa_id: input.companyId,
          entity_type: remote.entity_type,
          entity_id: remote.entity_id,
          parent_id: remote.parent_id,
          sequence: remote.sequence,
          deletion_kind: remote.deletion_kind,
          metadata: remote.metadata ? JSON.stringify(remote.metadata) : null,
          deleted_at: deletedAt,
          applied_at: Date.now(),
        });
        lastSequence = remote.sequence;
      }

      // A linha-envelope não contém tombstone nem cursor novo. Ela não pode
      // apagar um checkpoint terminal que ainda é válido para a próxima página.
      if (page.rows.length === 0) {
        if (pageNumber > 0 || storedCheckpoint?.active) {
          throw new Error('Envelope vazio de tombstones interrompeu uma página com continuidade.');
        }
        if (replayFromStart) {
          if (expectedTarget !== sequenceFloor) {
            throw new Error('Replay vazio de tombstones não comprovou o target persistido.');
          }
          cursor = null;
          lastSequence = sequenceFloor;
          await this.repository.saveCursor({
            id: identity.id,
            empresa_id: input.companyId,
            stream: encodeV2Checkpoint({
              cursor: null,
              targetHighWatermark: expectedTarget,
              active: false,
            }),
            sequence: sequenceFloor,
            updated_at: Date.now(),
            cursor: null,
            target_high_watermark: expectedTarget,
            active: false,
          });
        }
        const allRows = await this.repository.listTombstones(input.companyId);
        if (allRows.some(row => row.empresa_id !== input.companyId)) {
          throw new Error('Repositorio retornou tombstone de outra empresa.');
        }
        if (!replayFromStart && storedCheckpoint?.active === false && lastSequence < expectedTarget) {
          const terminalCheckpoint: LocalTombstoneCursor = {
            id: identity.id,
            empresa_id: input.companyId,
            stream: encodeV2Checkpoint({
              cursor,
              targetHighWatermark: expectedTarget,
              active: false,
            }),
            sequence: Math.max(sequenceFloor, expectedTarget),
            updated_at: Date.now(),
            cursor,
            target_high_watermark: expectedTarget,
            active: false,
          };
          await this.repository.saveCursor(terminalCheckpoint);
          lastSequence = Math.max(sequenceFloor, expectedTarget);
        }
        return {
          cursor: lastSequence,
          applied,
          duplicates,
          barriersByTable: buildBarriers(allRows),
          nextCursor: cursor,
          targetHighWatermark: expectedTarget,
          hasMore: false,
        };
      }

      const checkpointCursor = page.nextCursor ?? cursor;
      if (!checkpointCursor) {
        throw new Error('Página final de tombstones sem cursor de checkpoint.');
      }

      const checkpoint: LocalTombstoneCursor = {
        id: identity.id,
        empresa_id: input.companyId,
        stream: encodeV2Checkpoint({
          cursor: checkpointCursor,
          targetHighWatermark: expectedTarget,
          active: page.hasMore,
          replaySequence: replaying && page.hasMore ? lastSequence : undefined,
        }),
        sequence: Math.max(sequenceFloor, lastSequence),
        updated_at: Date.now(),
        cursor: checkpointCursor,
        target_high_watermark: expectedTarget,
        active: page.hasMore,
      };

      // A aplicação local precede o checkpoint. Sem transação de página, uma
      // falha no meio deixa a página parcialmente aplicada e o próximo ciclo
      // a reproduz desde o último checkpoint durável; evento e alvo são
      // idempotentes. Este contrato não promete atomicidade da página.
      const pageResult = await this.applyPageDurably(localRows, checkpoint);
      applied += pageResult.applied;
      duplicates += pageResult.duplicates;
      cursor = checkpointCursor;

      if (!page.hasMore) {
        const allRows = await this.repository.listTombstones(input.companyId);
        if (allRows.some(row => row.empresa_id !== input.companyId)) {
          throw new Error('Repositorio retornou tombstone de outra empresa.');
        }
        return {
          cursor: Math.max(sequenceFloor, lastSequence),
          applied,
          duplicates,
          barriersByTable: buildBarriers(allRows),
          nextCursor: checkpointCursor,
          targetHighWatermark: expectedTarget,
          hasMore: false,
        };
      }

      if (pageNumber === this.maxPages - 1) {
        throw new Error('Pull v2 de tombstones excedeu o limite seguro de paginas.');
      }
    }

    throw new Error('Pull v2 de tombstones não concluiu o ciclo.');
  }

  private async applyPageDurably(
    rows: LocalTombstoneRow[],
    cursor: LocalTombstoneCursor
  ): Promise<{ applied: number; duplicates: number }> {
    if (this.repository.applyPageDurably) {
      return this.repository.applyPageDurably({ rows, cursor });
    }

    let applied = 0;
    let duplicates = 0;
    for (const row of rows) {
      const existing = await this.repository.getTombstone(row.id);
      if (existing && !sameTombstone(existing, row)) {
        throw new Error('Tombstone local conflita com o evento remoto.');
      }
      if (!existing) {
        await this.repository.saveTombstone(row);
        applied += 1;
      } else {
        duplicates += 1;
      }
      await this.applyLocally(row);
    }

    await this.repository.saveCursor(cursor);
    return { applied, duplicates };
  }

  private async applyLocally(tombstone: LocalTombstoneRow): Promise<void> {
    const target = await this.repository.readTarget(tombstone.entity_type, tombstone.entity_id);
    if (!target) return;
    if (target.empresa_id !== tombstone.empresa_id) {
      throw new Error('Alvo local do tombstone pertence a outra empresa ou nao tem escopo valido.');
    }

    if (tombstone.deletion_kind === 'retire') {
      if (!RETIRABLE_TABLES.has(tombstone.entity_type)) {
        throw new Error('Aposentadoria recebida para tabela sem contrato retired_at.');
      }
      const retired = this.repository.retireTargetIfMatches
        ? await this.repository.retireTargetIfMatches(
          tombstone.entity_type,
          tombstone.entity_id,
          tombstone.deleted_at,
          target
      )
      : await this.repository.retireTarget(
        tombstone.entity_type,
        tombstone.entity_id,
        tombstone.deleted_at
      );
      if (!retired) {
        if (this.repository.retireTargetIfMatches) {
          return;
        }
        throw new Error('Falha ao aplicar aposentadoria local.');
      }
      return;
    }

    const deleted = this.repository.deleteTargetIfMatches
      ? await this.repository.deleteTargetIfMatches(
        tombstone.entity_type,
        tombstone.entity_id,
        target
      )
      : await this.repository.deleteTarget(tombstone.entity_type, tombstone.entity_id);
    if (!deleted) {
      if (this.repository.deleteTargetIfMatches) {
        return;
      }
      throw new Error('Falha ao aplicar exclusao local do tombstone.');
    }
  }
}

export const toSyncTombstone = (
  companyId: string,
  row: RemoteTombstoneRow
): SyncTombstone => ({
  id: row.event_id,
  companyId,
  table: row.entity_type,
  recordId: row.entity_id,
  sequence: row.sequence,
  deletedAt: row.deleted_at,
  parentId: row.parent_id,
  deletionKind: row.deletion_kind,
  metadata: row.metadata ?? {},
});
